import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Languages, Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { images } from "../data/images";
import SearchDialog from "./SearchDialog";

const Header = () => {
    const { lang, toggle, t } = useLanguage();
    const [openSearch, setOpenSearch] = useState(false);
    const [openMobile, setOpenMobile] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpenMobile(false);
    }, [location.pathname]);

    const navItems = [
        { to: "/", label: t.nav.home, testid: "nav-home" },
        { to: "/missions", label: t.nav.missions, testid: "nav-missions" },
        { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
    ];

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
                    scrolled
                        ? "bg-white/90 backdrop-blur-md border-b border-[var(--sodeco-border)]"
                        : "bg-white/0"
                }`}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="brand-logo"
                        className="flex items-center gap-3 group"
                    >
                        <span
                            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full overflow-hidden ring-2 ring-[var(--sodeco-yellow)] ring-offset-2 ring-offset-white transition-transform duration-500 group-hover:scale-105"
                            aria-hidden
                        >
                            {images.logoMark ? (
                                <img
                                    src={images.logoMark}
                                    alt="SODECO"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <span
                                    className="w-full h-full flex items-center justify-center font-serif text-sm font-semibold"
                                    style={{ background: "var(--sodeco-yellow)", color: "var(--sodeco-ink)" }}
                                >
                                    S
                                </span>
                            )}
                        </span>
                        <span className="font-serif text-2xl tracking-tight">
                            SODECO
                        </span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-10">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={item.testid}
                                end={item.to === "/"}
                                className={({ isActive }) =>
                                    `nav-link text-sm tracking-wide uppercase ${
                                        isActive ? "active font-medium" : "text-[var(--sodeco-muted)]"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 md:gap-3">
                        <button
                            data-testid="open-search-btn"
                            onClick={() => setOpenSearch(true)}
                            className="p-2 rounded-full hover:bg-[var(--sodeco-yellow-soft)] transition-colors"
                            aria-label={t.actions.search}
                        >
                            <Search size={18} />
                        </button>
                        <button
                            data-testid="lang-toggle-btn"
                            onClick={toggle}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--sodeco-border)] hover:border-[var(--sodeco-ink)] transition-colors text-xs font-medium uppercase tracking-wider"
                        >
                            <Languages size={14} />
                            {lang === "es" ? "ES / EN" : "EN / ES"}
                        </button>
                        <button
                            data-testid="mobile-menu-btn"
                            onClick={() => setOpenMobile((s) => !s)}
                            className="md:hidden p-2"
                            aria-label="menu"
                        >
                            {openMobile ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>

                {openMobile && (
                    <div
                        data-testid="mobile-menu"
                        className="md:hidden bg-white border-t border-[var(--sodeco-border)]"
                    >
                        <nav className="px-6 py-6 flex flex-col gap-4">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`${item.testid}-mobile`}
                                    end={item.to === "/"}
                                    className={({ isActive }) =>
                                        `text-lg font-serif ${
                                            isActive ? "text-[var(--sodeco-ink)]" : "text-[var(--sodeco-muted)]"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>
                )}
            </header>

            <SearchDialog open={openSearch} onClose={() => setOpenSearch(false)} />
        </>
    );
};

export default Header;
